import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../../../context/AuthContext';
import { getSite, updateSite, getSites } from '../../../models/sitesModel';
import { toast } from 'react-toastify';
import { useForm, Controller } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';

const schema = yup.object().shape({
  site_name: yup.string().required('Site name is required'),
  site_parent: yup.number().nullable(),
});

function SiteEdit() {
  const { siteId } = useParams();
  const navigate = useNavigate();
  const { can } = useAuth();
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [parentSites, setParentSites] = useState([]);

  const { control, handleSubmit, formState: { errors }, reset } = useForm({
    resolver: yupResolver(schema),
    defaultValues: {
      site_name: '',
      site_parent: 0,
    }
  });

  useEffect(() => {
    async function fetchData() {
      try {
        if (!can('Update Site')) {
          toast.error('You do not have permission to edit sites');
          navigate('/dashboard/sites');
          return;
        }

        setLoading(true);
        
        // Fetch site details and parent sites in parallel
        const [siteResponse, sitesResponse] = await Promise.all([
          getSite(siteId),
          getSites()
        ]);
        
        if (siteResponse.status === 1 && siteResponse.row) {
          // Reset form with fetched data
          reset({
            site_name: siteResponse.row.site_name || '',
            site_parent: siteResponse.row.site_parent || 0,
          });
        } else {
          toast.error('Site not found');
          navigate('/dashboard/sites');
          return;
        }
        
        if (sitesResponse.status === 1) {
          // Filter out current site from parent options to prevent circular reference
          const filteredSites = sitesResponse.rows?.filter(site => site.site_id != siteId) || [];
          setParentSites([{ site_id: 0, site_name: 'No Parent' }, ...filteredSites]);
        } else {
          setParentSites([{ site_id: 0, site_name: 'No Parent' }]);
        }
      } catch (error) {
        console.error('Error fetching site data:', error);
        toast.error('Error loading site data');
        navigate('/dashboard/sites');
      } finally {
        setLoading(false);
      }
    }

    fetchData();
  }, [siteId, navigate, can, reset]);

  const onSubmit = async (data) => {
    try {
      setSubmitting(true);
      const response = await updateSite(siteId, data);
      
      if (response.status === 1) {
        toast.success('Site updated successfully!');
        navigate('/dashboard/sites');
      } else {
        toast.error(response.message || 'Failed to update site');
      }
    } catch (error) {
      console.error('Error updating site:', error);
      toast.error('Error updating site. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const handleCancel = () => {
    navigate(-1);
  };

  const handleBack = useCallback(() => {
    navigate(-1);
  }, [navigate]);

  if (loading) {
    return (
      <div className="max-w-4xl mx-auto p-6">
        <div className="flex items-center justify-center py-12">
          <div className="flex justify-center items-center space-x-2">
            <svg className="animate-spin h-5 w-5 text-[#0087D4]" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
            </svg>
            <span>Loading site...</span>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto p-6">
      {/* Header with Back Navigation */}
      <div className="mb-6">
        <button
          onClick={handleBack}
          className="flex items-center text-[#0087D4] hover:text-blue-600 transition-colors mb-4"
        >
          <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
          </svg>
          Back
        </button>
        <h1 className="text-2xl font-semibold text-gray-900">Edit Site</h1>
      </div>

      <form onSubmit={handleSubmit(onSubmit)}>
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Left Column: Site Details */}
          <div className="lg:col-span-1 space-y-6">
            <div className="bg-white p-6 rounded-lg shadow border">
              <h3 className="text-lg font-medium text-gray-900 mb-4">Site Details</h3>
              <div className="space-y-4">
                <div>
                  <Controller
                    name="site_parent"
                    control={control}
                    render={({ field }) => (
                      <div>
                        <label htmlFor="site_parent" className="block text-sm font-medium text-gray-700">
                          Parent Site
                        </label>
                        <select
                          id="site_parent"
                          className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-[#0087D4] focus:border-[#0087D4] sm:text-sm"
                          {...field}
                        >
                          {parentSites.map((site) => (
                            <option key={site.site_id} value={site.site_id}>
                              {site.site_name}
                            </option>
                          ))}
                        </select>
                        {errors.site_parent && (
                          <p className="mt-1 text-sm text-red-600">{errors.site_parent.message}</p>
                        )}
                        <p className="mt-1 text-sm text-gray-500">
                          Select a parent site to create a hierarchy, or leave as "No Parent" for a top-level site.
                        </p>
                      </div>
                    )}
                  />
                </div>
              </div>
            </div>

            {/* Actions */}
            <div className="bg-white p-6 rounded-lg shadow border">
              <h3 className="text-lg font-medium text-gray-900 mb-4">Actions</h3>
              <div className="flex flex-col gap-3">
                <button 
                  type="submit" 
                  className="inline-flex items-center justify-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-[#0087D4] hover:bg-blue-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#0087D4] w-full"
                  disabled={submitting}
                >
                  {submitting ? (
                    <>
                      <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                      </svg>
                      Updating...
                    </>
                  ) : 'Update Site'}
                </button>
                <button 
                  type="button" 
                  className="inline-flex items-center justify-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#0087D4] w-full"
                  onClick={handleCancel}
                  disabled={submitting}
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>

          {/* Right Column: Site Content */}
          <div className="lg:col-span-2">
            <div className="bg-white p-6 rounded-lg shadow border">
              <h3 className="text-lg font-medium text-gray-900 mb-4">Site Information</h3>
              <div className="space-y-4">
                <div>
                  <Controller
                    name="site_name"
                    control={control}
                    render={({ field }) => (
                      <div>
                        <label htmlFor="site_name" className="block text-sm font-medium text-gray-700">
                          Site Name *
                        </label>
                        <input
                          id="site_name"
                          type="text"
                          className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-[#0087D4] focus:border-[#0087D4] sm:text-sm"
                          placeholder="Enter site name"
                          {...field}
                        />
                        {errors.site_name && (
                          <p className="mt-1 text-sm text-red-600">{errors.site_name.message}</p>
                        )}
                      </div>
                    )}
                  />
                </div>

                <div className="bg-gray-50 p-4 rounded-md">
                  <h4 className="text-sm font-medium text-gray-900 mb-2">Site Hierarchy</h4>
                  <p className="text-sm text-gray-600">
                    Sites can be organized in a hierarchy. Select a parent site to place this site under it in the organizational structure. This helps in managing permissions and organizing your sites logically.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </form>
    </div>
  );
}

export default SiteEdit;